import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useDestinations } from './destinations';
import * as selectedBrandStorage from '@lib/selected-brand-storage';

function storageKey(): string {
  return `selected-destination:${selectedBrandStorage.read() ?? ''}`;
}

function readPersisted(): number | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey());
    const parsed = raw == null ? NaN : parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  } catch {
    // No window/localStorage (tests, storage-disabled contexts) — behave as "nothing persisted".
    return undefined;
  }
}

function persist(id: number | undefined): void {
  try {
    if (id == null) window.localStorage.removeItem(storageKey());
    else window.localStorage.setItem(storageKey(), String(id));
  } catch {
    // Persistence is best-effort.
  }
}

/**
 * The destination selected in pricing UIs, shared between the price popper, the SKU pricing
 * table and anything else that shows destination-sensitive numbers. Falls back to the brand's
 * default destination; persisted per brand in localStorage.
 */
export const useSelectedDestination = defineStore('selected-destination', () => {
  const destinations = useDestinations();

  const selectedDestinationId = ref<number | undefined>(readPersisted());

  const selectedDestination = computed(
    () =>
      (selectedDestinationId.value != null ? destinations.getById(selectedDestinationId.value) : undefined) ??
      destinations.defaultDestination
  );

  function select(id: number | undefined) {
    selectedDestinationId.value = id;
    persist(id);
  }

  // If the persisted destination no longer exists (deleted, or a brand switch), reset to default.
  watch(
    () => destinations.loaded,
    () => {
      if (
        selectedDestinationId.value != null &&
        destinations.isLoaded &&
        !destinations.getById(selectedDestinationId.value)
      ) {
        select(undefined);
      }
    }
  );

  return { selectedDestinationId, selectedDestination, select };
});
